import { useState, type ReactNode } from 'react';
import { Box, Button, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import IosShareIcon from '@mui/icons-material/IosShare';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { ConfidencePill, PlanChip, type PlanChipTone } from './components';
import { COLORS } from './tokens';
import type { PlanRecordYear } from './planRecordApi';
import { displayYear } from './planRecordView';

export type PlanYearMenu = { years: PlanRecordYear[]; selected: string; onSelect: (year: string) => void };

export function YearPill({ label }: { label: string }) {
  return (
    <Typography
      component="span"
      sx={{ fontWeight: 600, fontSize: 16, lineHeight: '24px', color: COLORS.ink, border: `1px solid ${COLORS.divider}`, borderRadius: 2, px: 1, py: 0.25 }}
    >
      {displayYear(label)}
    </Typography>
  );
}

export function YearMenu({ menu }: { menu: PlanYearMenu }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <Button
        onClick={(e) => setAnchor(e.currentTarget)}
        endIcon={<KeyboardArrowDownIcon sx={{ color: 'rgba(8,8,27,0.54)' }} />}
        aria-haspopup="listbox"
        sx={{ textTransform: 'none', fontWeight: 600, fontSize: 16, lineHeight: '24px', color: COLORS.ink, border: `1px solid ${COLORS.divider}`, borderRadius: 2, pl: 1, pr: 0.5, py: 0, minHeight: 0 }}
      >
        {displayYear(menu.selected)}
      </Button>
      <Menu open={anchor !== null} anchorEl={anchor} onClose={() => setAnchor(null)} MenuListProps={{ role: 'listbox' }}>
        {menu.years.map((y) => (
          <MenuItem
            key={y.year}
            role="option"
            selected={y.year === menu.selected}
            onClick={() => {
              setAnchor(null);
              if (y.year !== menu.selected) menu.onSelect(y.year);
            }}
            sx={{ display: 'flex', justifyContent: 'space-between', gap: 3, fontSize: 16 }}
          >
            <span>{displayYear(y.year)}</span>
            <Typography component="span" sx={{ fontSize: 12, fontWeight: 600, color: COLORS.muted }}>
              {y.status === 'finalized' ? 'Finalized' : 'Draft'}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export function PlanHeader({
  fullName,
  statusLabel,
  chip,
  altChip = false,
  yearMenu,
  yearLabel,
  subline,
  actions,
  showShare = true,
}: {
  fullName: string;
  statusLabel?: string;
  chip?: { label: string; tone: PlanChipTone };
  altChip?: boolean;
  yearMenu?: PlanYearMenu;
  yearLabel?: string;
  subline?: string;
  actions?: ReactNode;
  showShare?: boolean;
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2.5 }}>
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: '0.02em',
              color: COLORS.muted,
            }}
          >
            Student Action Plan
          </Typography>
          {yearMenu ? <YearMenu menu={yearMenu} /> : yearLabel && <YearPill label={yearLabel} />}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: 32, fontWeight: 700, color: COLORS.ink, lineHeight: 1.2 }}>
            {fullName}
          </Typography>
          {chip ? <PlanChip label={chip.label} tone={chip.tone} /> : statusLabel && <ConfidencePill label={statusLabel} />}
          {altChip && <PlanChip label="Alternate ACCESS" />}
        </Box>
        {subline && (
          <Typography sx={{ fontSize: 14, fontWeight: 500, color: COLORS.muted, mt: 0.75 }}>
            {subline}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 2.5, flexShrink: 0 }}>
        <IconButton size="small" sx={{ color: 'rgba(8,8,27,0.54)' }} aria-label="Helpful">
          <ThumbUpAltOutlinedIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" sx={{ color: 'rgba(8,8,27,0.54)' }} aria-label="Not helpful">
          <ThumbDownAltOutlinedIcon fontSize="small" />
        </IconButton>
        <Box sx={{ width: '1px', height: 24, bgcolor: COLORS.hairline, mx: 0.5 }} />
        <Button
          startIcon={<PrintOutlinedIcon />}
          sx={{
            textTransform: 'none',
            color: COLORS.ink,
            bgcolor: '#FFFFFF',
            border: `1px solid ${COLORS.hairline}`,
            fontWeight: 600,
            px: 2,
            '&:hover': { bgcolor: '#F0F0F5' },
          }}
        >
          Print
        </Button>
        {actions}
        {showShare && (
          <Button
            variant="contained"
            startIcon={<IosShareIcon />}
            sx={{
              textTransform: 'none',
              bgcolor: COLORS.navy,
              fontWeight: 700,
              px: 2,
              '&:hover': { bgcolor: '#23236F' },
            }}
          >
            Share
          </Button>
        )}
      </Box>
    </Box>
  );
}
